// src/components/loginforms/SignInForm.js
import React, { useState, useEffect } from 'react';
import styles from '../Header.module.css';
import { useAuth } from '../../context/AuthProvider';

const SignInForm = () => {
  const [mode, setMode] = useState('signIn'); // 'signIn' or 'reset'
  const [formData, setFormData] = useState({
    identifier: '', // username or email
    password: '',
    resetEmail: ''
  });
  const [rememberMe, setRememberMe] = useState(true);
  const [localError, setLocalError] = useState('');
  const [resetMessage, setResetMessage] = useState('');
  
  const { 
    signIn, 
    signInWithGoogle, 
    error: authError, 
    loading, 
    resetPassword 
  } = useAuth();

  // Clear error messages when changing form inputs
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
    setLocalError('');
  };

  // Handle sign in with email/password or username
  const handleSignIn = async (e) => {
    e.preventDefault();
    
    // Basic form validation
    if (!formData.identifier.trim()) {
      setLocalError('Please enter your username or email');
      return;
    }
    
    if (!formData.password) {
      setLocalError('Please enter your password');
      return;
    }
    
    try {
      // Call sign in from auth context - lowercase conversion handled in AuthProvider
      await signIn(formData.identifier, formData.password, rememberMe);
    } catch (error) {
      // Error is handled by auth context's useEffect
      console.error("Sign in error:", error);
    }
  };

  // Handle Google sign in
  const handleGoogleSignIn = (e) => {
    e.preventDefault(); // Prevent form submission
    setLocalError('');
    
    // Clear any existing auth state to prevent conflicts
    if (typeof window !== "undefined") {
      // Clear session storage items
      Object.keys(sessionStorage).forEach((key) => {
        if (
          key.startsWith("firebaseui:") ||
          (key.startsWith("firebase:auth:") && !key.includes(window.location.hostname))
        ) {
          console.log("Removing stale session item:", key);
          sessionStorage.removeItem(key);
        }
      });
      
      // Set auth domain for this session
      sessionStorage.setItem("firebase:AuthEventDomain", window.location.hostname);
    }
    
    try {
      signInWithGoogle();
    } catch (err) {
      console.error("Google sign-in error in form handler:", err);
      setLocalError('Failed to start Google sign-in. Please try again.');
    }
  };

  // Handle password reset request
  const handleReset = async (e) => {
    e.preventDefault();
    setLocalError('');
    setResetMessage('');
    
    if (!formData.resetEmail.trim()) {
      setLocalError('Please enter your email address');
      return;
    }
    
    try {
      const success = await resetPassword(formData.resetEmail);
      if (success) {
        //setResetMessage('Password reset email sent. Please check your inbox.');
        // Add the alert here:
      alert("Password reset email sent! Check your inbox and spam folders.");
        // Clear the form field after success
        setFormData(prev => ({...prev, resetEmail: ''}));
      }
    } catch (error) {
      setLocalError('Failed to send reset email. Please try again');
    }
  };
  
  // Show error from auth context if exists
  useEffect(() => {
    if (authError) {
      setLocalError(authError);
    }
  }, [authError]);

  return (
    <div className={styles.signInContent}>
      {mode === 'signIn' ? (
        <>
          <div className={styles.authFormHeader}>
            <h2>Welcome back</h2>
            <p>Sign in to manage your Linkist profile.</p>
          </div>

          <button
            type="button"
            onClick={handleGoogleSignIn}
            className={`${styles.googleButton} ${styles.animate}`}
            disabled={loading}
          >
            <img
              src="/assets/google-icon.svg"
              alt="Google Icon"
              className={styles.googleIcon}
            />
            <span>{loading ? 'Signing in...' : 'Continue with Google'}</span>
          </button>
          
          <div className={styles.divider}>or</div>
          
          <form onSubmit={handleSignIn}>
            <input
              type="text"
              name="identifier"
              placeholder="Username or email"
              value={formData.identifier}
              onChange={handleInputChange}
              className={styles.usernameInput}
              disabled={loading}
            />
            
            <input
              type="password"
              name="password"
              placeholder="Password"
              value={formData.password}
              onChange={handleInputChange}
              disabled={loading}
            />
            
            <div className={styles.rememberMeContainer}>
              <label className={styles.rememberMeLabel}>
                <input 
                  type="checkbox" 
                  checked={rememberMe} 
                  onChange={(e) => setRememberMe(e.target.checked)} 
                  disabled={loading}
                />
                Remember me
              </label>
            </div>
            
            {localError && (
              <div className={styles.errorText}>{localError}</div>
            )}
            
            <button type="submit" disabled={loading}>
              {loading ? 'Signing in...' : 'Sign in'}
            </button>
          </form>
          
          <p className={styles.forgotPassword}>
            Forgot your password?{' '}
            <button 
              type="button"
              onClick={() => {
                setMode('reset');
                setLocalError('');
              }} 
              disabled={loading}
            >
              Reset password
            </button>
          </p>
        </>
      ) : (
        <>
          <div className={styles.authFormHeader}>
            <h2 className={styles.resetHeader}>Reset your password</h2>
            <p>Enter your account email and we'll send a reset link.</p>
          </div>
          
          <form onSubmit={handleReset}>
            <input
              type="email"
              name="resetEmail"
              placeholder="Email address"
              value={formData.resetEmail}
              onChange={handleInputChange}
              disabled={loading}
            />
            
            {localError && (
              <div className={styles.errorText}>{localError}</div>
            )}
            
            <button type="submit" disabled={loading}>
              {loading ? 'Sending...' : 'Send reset email'}
            </button>
          </form>
          
          {resetMessage && (
            <div className={styles.successText}>{resetMessage}</div>
          )}
          
          <p className={styles.forgotPassword}>
            Remembered your password?{' '}
            <button 
              type="button"
              onClick={() => {
                setMode('signIn');
                setLocalError('');
                setResetMessage('');
              }}
              disabled={loading}
            >
              Sign in
            </button>
          </p>
        </>
      )}
    </div>
  );
};

export default SignInForm;
